import { motion } from 'framer-motion'
import { Zap, Volume2, Flame, Droplet, Shield, Eye, Package } from 'lucide-react'

// Données techniques mises à jour
const technicalSpecs = [
  {
    id: 1,
    icon: 'thermal',
    title: 'Isolation Thermique',
    value: '0,045 W/m·K',
    details: [
      'Résistance thermique : R = 2,2 avec 1 cm',
      'Réduction jusqu\'à 60% de la consommation énergétique',
      'Performance exceptionnelle pour seulement 1 cm d\'épaisseur'
    ]
  },
  {
    id: 2,
    icon: 'sound',
    title: 'Isolation Acoustique',
    value: 'αw = 0,35',
    details: [
      'Indice d\'affaiblissement acoustique : 0,19 avec 1 cm',
      'Réduction significative des bruits aériens et d\'impact',
      'Confort acoustique optimal'
    ]
  },
  {
    id: 3,
    icon: 'fire',
    title: 'Résistance au Feu',
    value: 'Classement A2',
    details: [
      'Classification A2-s1, d0 selon les normes européennes',
      'Non combustible - Sécurité incendie maximale',
      'Sans émissions toxiques en cas d\'incendie'
    ]
  },
  {
    id: 4,
    icon: 'adhesion',
    title: 'Régulation Humidité',
    value: '-10%',
    details: [
      'Réduction jusqu\'à 10% de l\'humidité relative',
      'Perméabilité à la vapeur d\'eau optimale',
      'Prévient la condensation et les moisissures'
    ]
  }
]

const iconMap = {
  thermal: Zap,
  sound: Volume2,
  fire: Flame,
  adhesion: Droplet
}

const Technical = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-gradient mb-6">Spécifications Techniques</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Performances exceptionnelles certifiées pour répondre aux normes les plus strictes de l'industrie.
          </p>
        </motion.div>

        {/* Spécifications principales */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {technicalSpecs.map((spec, index) => {
            const Icon = iconMap[spec.icon]
            return (
              <motion.div
                key={spec.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg card-hover"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{spec.title}</h3>
                    <div className="text-3xl font-bold text-green-600 mb-4">{spec.value}</div>
                    <ul className="space-y-2">
                      {spec.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-600">
                          <span className="text-green-600 mt-1">✓</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Avantages techniques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">Avantages Techniques</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Économie d\'énergie', value: '30%', desc: 'Réduction de la consommation énergétique' },
              { title: 'Épaisseur d\'application', value: '1cm', desc: 'Gain d\'espace optimal' },
              { title: 'Durée de vie', value: '50+', desc: 'Années de protection garantie' },
              { title: 'Adhésion', value: '0.33 N/mm²', desc: 'Sur tous types de surfaces' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md text-center"
              >
                <div className="text-4xl font-bold text-green-600 mb-2">{item.value}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Informations de sécurité et stockage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
        >
          {/* Sécurité */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-orange-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Sécurité et Précautions</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 text-sm font-bold">!</span>
                </div>
                <p className="text-gray-700">Tenir hors de portée des enfants</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 text-sm font-bold">!</span>
                </div>
                <p className="text-gray-700">Utiliser un masque et des lunettes de protection pendant l'application</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 text-sm font-bold">!</span>
                </div>
                <p className="text-gray-700">En cas de contact avec les yeux, rincer abondamment à l'eau</p>
              </div>
            </div>
          </div>

          {/* Stockage et conservation */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-blue-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Stockage et Conservation</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-bold">⏱️</span>
                </div>
                <p className="text-gray-700">Durée de conservation : 12 mois dans l'emballage d'origine scellé</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-bold">🏭</span>
                </div>
                <p className="text-gray-700">Conserver dans un endroit sec, à l'abri de l'humidité et du soleil</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm font-bold">⚖️</span>
                </div>
                <p className="text-gray-700">Poids net : 20 kg / 40 L</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Certifications et informations fabricant */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="bg-white rounded-2xl p-8 shadow-lg"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Certifications et Informations</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Certifications */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Certifications et Normes</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🏆</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Norme A2 Incendie</h4>
                    <p className="text-gray-600 text-sm">Classement A2-s1, d0 pour la résistance au feu selon les normes européennes</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🌍</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Certification Écologique</h4>
                    <p className="text-gray-600 text-sm">Produits 100% naturels certifiés sans substances nocives</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">✅</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Conformité CE</h4>
                    <p className="text-gray-600 text-sm">Conforme aux directives européennes de construction</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations fabricant */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Fabricant</h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🏭</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">ISOPLUS INSAAT</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      ÇOMAKLI MAH, HENDEK CAD 188/A<br />
                      MERAM / KONYA / TÜRKİYE
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Technical